import React, { useCallback, useEffect, useState } from "react";
import { useFormik } from "formik";
import DOMPurify from "dompurify";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Form, Row, Spinner, Tab, Tabs } from "react-bootstrap";
import { VscReplace } from "react-icons/vsc";
import { BiEdit } from "react-icons/bi";
import { GoAlert } from "react-icons/go";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { signalrActions } from "../../redux/signalr";
import HtmlEditor from "../../components/UI/HtmlEditorInput/HtmlEditor";
import SelectDiretor from "../../components/UI/SelectInput/SelectDiretor";
import SelectInputCachedFormik from "../../components/UI/SelectInput/SelectInputCachedFormik";
import useSetGuard from "../../hooks/useSetGuard";
import TextInputFormik from "../../components/UI/TextInput/TextInputFormik";
import funcionalidades from "../../context/AuthContext.funcionalidades";
import useApiClient, { apiRoutes } from "../../hooks/apiClient";
import ListaExecutores from "./ListaExecutores";
import ButtonSubmitFormikPermission from "../../components/UI/Button/ButtonSubmitFormikPermission";
import FormAlert from "../../components/UI/FormAlert/FormAlert";
import useBuscaTextoPadraoOs from "./hooks/useBuscaTextoPadraoOs";
import EfisModal from "../../components/UI/EfisModal/EfisModal";
import ReplaceChar from "../../components/Formatters/ReplaceChar";

const validationSchema = Yup.object().shape({
  // listaSpf: Yup.array().default([]).required("Informe pelo menos 1 SPF"),
  sequencialTipoAcao: Yup.string().default(""),
  textoPadrao: Yup.string()
    .default("")
    .test("texto-padrao", "Informe o Texto Complementar", (value) => {
      if ((value || []).length === 0 || value === undefined || value === null || value === "<p><br></p>") return false;

      return true;
    }),
  quantidadeDiasOs: Yup.number()
    .required("Informe a quantidade máxima de dias da OS")
    .positive()
    .integer()
    .min(1, "Valor mínimo deve ser 1")
    .max(999, "Valor máximo deve ser 999"),
  codigoDiretor: Yup.string().default("").required("Informe o Diretor"),
  listaExecutoresOs: Yup.array()
    .default([])
    .test("qtd-executor", "Selecione pelo menos 1 executor", (value) => (value || []).length > 0),
  textoPadraoOs: Yup.string().default(""),
});

const initialValues = {};

validationSchema._nodes.forEach((key) => {
  if (key === "listaSpf") {
    initialValues[key] = [];
  } else if (key === "quantidadeDiasOs") {
    initialValues[key] = 60;
  } else {
    initialValues[key] = "";
  }
});

const FormSpfGerarOsLote = ({ history, location }) => {
  const { state } = location;
  const [sqOrgao, setSqOrgao] = useState();
  const [sgOrgao, setSgOrgao] = useState("");
  const [listaOs, setListaOs] = useState([]);

  const [showTextoOs, setShowTextoOs] = useState(true);
  const [showViewSubstituicao, setShowViewSubstituicao] = useState(false);
  const [textoBody, setTextoBody] = useState("");
  const [desabilitarBotao, setDesabilitarBotao] = useState(false);

  const setGuardClient = useSetGuard();
  const { filaAssinaturaSucessoStateKey, filaAssinaturaFalhaStateKey } =
    useSelector((stateSignalr) => stateSignalr.signalr) || {};

  const dispatch = useDispatch();

  const { request: requestForm, data: dataForm, errors: errorForm, isLoading } = useApiClient();

  const handleSubmit = useCallback(
    (values) => {
      requestForm(apiRoutes.fiscalizacao.ordemservico.inserirOsSpf, {
        listaSpf: state.listaSpf,
        codigoDiretor: values.codigoDiretor,
        listaExecutoresOs: values.listaExecutoresOs,
        quantidadeDiasOs: values.quantidadeDiasOs,
        textoPadrao: ReplaceChar(values.textoPadrao),
        textoPadraoOs: ReplaceChar(values.textoPadraoOs),
      });
    },
    [requestForm, state.listaSpf],
  );

  const { request: requestSafePayload, data: dataSafePayload } = useApiClient();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  const { textoPadrao } = useBuscaTextoPadraoOs({ formik });

  const { setSubmitting, setErrors, errors, isSubmitting, isValid, setValues, setFieldValue, values } = formik;

  useEffect(() => {
    if (state.sequencialTipoAcao !== null) {
      setFieldValue("sequencialTipoAcao", state.sequencialTipoAcao);
    }
  }, [setFieldValue, state.sequencialTipoAcao]);

  useEffect(() => {
    if (dataForm?.result.isSuccess) {
      const listaTicket = [];
      const listaOS = [];

      dataForm?.result.value.forEach((x) => {
        listaTicket.push(x.ticket);
        listaOS.push(x.nuOrdemServico);
      });

      setListaOs(listaOS);
      requestSafePayload(apiRoutes.usuarios.arquivo.selecionar, listaTicket);
    } else {
      toast.error(dataForm?.result.error);
    }
  }, [dataForm, requestSafePayload]);

  useEffect(() => {
    if ((dataForm || errorForm) && isSubmitting && !isLoading) setSubmitting(false);

    if (errorForm && isValid && !isLoading) setErrors({ ...errors, ...errorForm });

    if (errors && isSubmitting) {
      if (errors.quantidadeDiasOs) {
        toast.warning(errors.quantidadeDiasOs);
      }
      if (errors.textoPadrao) {
        toast.warning(errors.textoPadrao);
      }
      if (errors.codigoDiretor) {
        toast.warning(errors.codigoDiretor);
      }

      if (errors.listaExecutoresOs) {
        toast.warning(errors.listaExecutoresOs);
      }
    }
  }, [setSubmitting, setErrors, errors, isSubmitting, isValid, dataForm, errorForm, isLoading]);

  const assinaturaHandler = useCallback(
    (safePayload) => {
      setGuardClient.assinar(safePayload);
    },
    [setGuardClient],
  );

  useEffect(() => {
    if (dataSafePayload) {
      assinaturaHandler(dataSafePayload.result);
    }
  }, [dataSafePayload, assinaturaHandler]);

  useEffect(() => {
    if (filaAssinaturaSucessoStateKey) {
      const quantidade = state.listaSpf.length;
      let texto = "";
      if (quantidade > 1) {
        texto = `Ordens de Serviço ${listaOs} geradas com sucesso.`;
      } else {
        texto = `Ordem de Serviço ${listaOs} gerada com sucesso.`;
      }

      toast.success(texto);
      history.push("/app/os/gerarOs");
    }
    return () => {
      dispatch(signalrActions.clearState("filaAssinaturaSucessoStateKey"));
    };
  }, [dispatch, filaAssinaturaSucessoStateKey, history, state.listaSpf.length, listaOs]);

  useEffect(() => {
    dispatch(signalrActions.clearState("filaAssinaturaFalhaStateKey"));
  }, [dispatch, filaAssinaturaFalhaStateKey]);

  // Trazer orgão do usuário logado
  const { request: requestOrgao, data: dataOrgao, errors: errorOrgao, isLoading: isLoadingOrgao } = useApiClient();

  useEffect(() => {
    requestOrgao(apiRoutes.fiscalizacao.ordemservico.consultaOrgaoLogado);
  }, [requestOrgao]);

  useEffect(() => {
    if ((!dataOrgao && !errorOrgao) || isLoadingOrgao) return;
    if (dataOrgao && dataOrgao.result.length) {
      setSqOrgao(dataOrgao.result[0].sequencialOrgao);
      setSgOrgao(dataOrgao.result[0].siglaOrgao);
    }
  }, [dataOrgao, errorOrgao, isLoadingOrgao]);
  // -------------

  useEffect(() => {
    setValues((curr) => ({
      ...curr,
      sequencialTipoAcao: state.sequencialTipoAcao,
      textoPadraoOs: textoPadrao,
    }));
  }, [setValues, textoPadrao, state.sequencialTipoAcao, values.textoPadraoOs]);

  return (
    <>
      {setGuardClient.guardLink}
      <EfisModal
        show={showViewSubstituicao}
        title="Texto da OS"
        size="lg"
        closeHandler={() => setShowViewSubstituicao(false)}
        primaryLabelVisible={false}
        secondaryLabel="Fechar"
        body={<span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(textoBody) }} />}
      />
      <h1 className="h3 mb-4 text-gray-800">Gerar Ordem de Serviço</h1>
      {/* <CheckBoxFormik inline="inline" type="checkbox" formik={formik} field="fgAssinar" label="Assinar digitalmente" /> */}
      <div className="row">
        <div className="col-lg-12">
          {/* Conteúdo personalizado (card) */}
          <div className="card shadow mb-4">
            <div className="card-header py-3">
              <Row>
                <Col sm="11">
                  <h6 className="m-0 font-weight-bold text-primary flex-grow-1"> A partir de SPF</h6>
                </Col>
              </Row>
            </div>
            <div className="card-body">
              <FormAlert formik={formik} data={dataForm} errors={errorForm} isLoading={isLoading} />
              <Form noValidate onSubmit={formik.handleSubmit}>
                <Tabs defaultActiveKey="geral" transition={false} id="tabSpf">
                  <Tab
                    eventKey="geral"
                    title={
                      <span>
                        <GoAlert
                          color="red"
                          size={15}
                          hidden={
                            !(
                              formik.errors.textoPadrao ||
                              formik.errors.quantidadeDiasOs ||
                              formik.errors.codigoDiretor
                            )
                          }
                        />
                        &nbsp;Dados Gerais
                      </span>
                    }
                  >
                    <Row>
                      <Form.Group as={Col} sm={12} md={3}>
                        <Form.Label>
                          {`Quantidade de SPF's marcadas: ${state.listaSpf.length > 0 ? state.listaSpf.length : 0}`}
                        </Form.Label>
                      </Form.Group>
                    </Row>
                    {sgOrgao === "COFIS" ? (
                      <Row>
                        <Form.Group as={Col} sm={12} md={6}>
                          <Form.Label>Setor</Form.Label>
                          <SelectInputCachedFormik formik={formik} field="setor" tipoItem="orgaos-os" />
                        </Form.Group>
                      </Row>
                    ) : null}
                    <Row>
                      <Form.Group as={Col} sm={12} md={6}>
                        <SelectDiretor formik={formik} field="codigoDiretor" sqorgaologado={sqOrgao} />
                      </Form.Group>
                      <Form.Group as={Col} sm={12} md={3}>
                        <Form.Label>N° Máximo de Dias da OS</Form.Label>
                        <TextInputFormik formik={formik} field="quantidadeDiasOs" maxLength="3" type="number" />
                      </Form.Group>
                    </Row>

                    <Row>
                      <Form.Group as={Col} sm={12} md={12}>
                        <Form.Label>Texto Complementar</Form.Label>
                        <HtmlEditor formik={formik} field="textoPadrao" />
                      </Form.Group>
                    </Row>
                    <Row>
                      <Col>
                        <hr />
                        <Form.Label>
                          <strong>Texto da OS</strong>
                          <BiEdit
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title={showTextoOs ? "Editar Texto" : "Fechar Edição"}
                            onClick={() => {
                              setShowTextoOs(!showTextoOs);
                            }}
                          />
                          <VscReplace
                            className="btn-lg  text-danger pa-0 p-0 btn btn-bg-transparent ml-2"
                            title="Visualizar texto com substituições"
                            onClick={() => {
                              setShowViewSubstituicao(true);
                              setTextoBody(formik.values.textoPadraoOs);
                            }}
                          />
                        </Form.Label>
                        {!showTextoOs && <HtmlEditor formik={formik} field="textoPadraoOs" rows="5" />}
                        <hr hidden={!showTextoOs} />
                        <br />
                      </Col>
                    </Row>
                  </Tab>
                  <Tab
                    eventKey="listaExecutoresOs"
                    title={
                      <span>
                        <GoAlert color="red" size={15} hidden={!formik.errors.listaExecutoresOs} />
                        &nbsp;Executores
                      </span>
                    }
                  >
                    <ListaExecutores formik={formik} field="listaExecutoresOs" />
                  </Tab>
                </Tabs>

                <Row>
                  <Col>
                    {formik.isSubmitting ? (
                      <span>
                        <Spinner animation="border" role="status" /> Gerando Ordem de Serviço a partir de SPF (em
                        Lote)...
                      </span>
                    ) : (
                      <ButtonSubmitFormikPermission
                        permission={funcionalidades.ordemservico.inserir}
                        disabled={desabilitarBotao || !formik.isValid}
                        name="Gerar OS"
                        onClick={() => setDesabilitarBotao(true)}
                      />
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="light" className="mr-3" onClick={formik.handleReset}>
                        Limpar
                      </Button>
                    )}
                    {"    "}
                    {!formik.isSubmitting && (
                      <Button variant="danger" onClick={() => history.goBack()}>
                        Cancelar
                      </Button>
                    )}
                  </Col>
                </Row>
              </Form>
            </div>
          </div>
        </div>
      </div>
      {/* fim da row */}
    </>
  );
};

export default FormSpfGerarOsLote;
